// typeAppearPolicy.js — named Appear-as rows on a type

import { getOptionLabel, getSelectableEnumOptions } from 'utilities/EnumOptions';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';
import { toLowerSnakeCase } from 'utilities/Strings';
import { typeShowsInAddMenu } from 'utilities/typeDisplayPolicy';
import { buildProjectAppearAsRows } from 'utilities/PipelineStages';

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeAppearAsKey(value) {
  const cleaned = toLowerSnakeCase(String(value || '').trim());
  return cleaned.replace(/[^a-z0-9_]/g, '') || '';
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * @param {unknown} value
 * @returns {string[]}
 */
function normalizeStringList(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeString(entry)).filter(Boolean);
  }
  const single = normalizeString(value);
  return single ? [single] : [];
}

export const COLLECTION_SURFACE_OVERVIEW = 'overview';
export const COLLECTION_SURFACE_RECORDS = 'records';

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeAppearAsDefaultSurface(value) {
  const next = String(value || '').trim().toLowerCase();
  if (next === COLLECTION_SURFACE_OVERVIEW) return COLLECTION_SURFACE_OVERVIEW;
  if (next === COLLECTION_SURFACE_RECORDS) return COLLECTION_SURFACE_RECORDS;
  return '';
}

/**
 * Landing surface when opening a collection, optionally through Appear as.
 *
 * @param {object|null|undefined} appearAs
 * @param {{ hasOverview?: boolean }} [options]
 * @returns {string}
 */
export function resolveAppearAsCollectionSurface(appearAs, { hasOverview = false } = {}) {
  if (!hasOverview) return COLLECTION_SURFACE_RECORDS;
  const explicit = normalizeAppearAsDefaultSurface(appearAs?.defaultSurface);
  if (explicit) return explicit;
  const appearAsKey = normalizeAppearAsKey(appearAs?.key);
  if (appearAsKey === 'lead' || appearAsKey === 'project') return COLLECTION_SURFACE_RECORDS;
  return COLLECTION_SURFACE_OVERVIEW;
}

/**
 * @param {object|null|undefined} filter
 * @returns {object|null}
 */
export function normalizeAppearAsFilter(filter, { requireValue = true } = {}) {
  if (!filter || typeof filter !== 'object') return null;

  const field = normalizeString(filter.field || filter.fieldKey);
  if (!field) return null;

  const single = normalizeStringList(filter.value)[0] || '';
  if (requireValue && !single) return null;

  return {
    field,
    operator: 'is',
    value: single,
    unit: null,
    type: normalizeString(filter.type) || 'enum',
    joinToNext: 'and',
  };
}

/**
 * @param {object|null|undefined} row
 * @param {{ requireLabel?: boolean }} [options]
 * @returns {object|null}
 */
export function normalizeAppearAsRow(row, {
  requireLabel = true,
  requireFilterValues = requireLabel,
} = {}) {
  if (!row || typeof row !== 'object') return null;

  const key = normalizeAppearAsKey(row.key || row.label);
  const label = normalizeString(row.label);
  if (!key) return null;
  if (requireLabel && !label) return null;

  const initialFields = {};
  if (row.initialFields && typeof row.initialFields === 'object' && !Array.isArray(row.initialFields)) {
    Object.entries(row.initialFields).forEach(([fieldKey, value]) => {
      const name = normalizeString(fieldKey);
      if (!name || value === undefined || value === null || value === '') return;
      initialFields[name] = Array.isArray(value) ? normalizeString(value[0]) : value;
    });
  }

  const rawFilter = Array.isArray(row.filters) ? row.filters[0] : null;
  const initialEntry = Object.entries(initialFields)[0] || [];
  const field = normalizeString(rawFilter?.field || rawFilter?.fieldKey || initialEntry[0]);
  const value = normalizeString(initialEntry[1])
    || normalizeStringList(rawFilter?.value)[0]
    || '';
  const filters = field
    ? [
      normalizeAppearAsFilter({
        field,
        operator: 'is',
        value,
        type: 'enum',
      }, { requireValue: requireFilterValues }),
    ].filter(Boolean)
    : [];

  return {
    key,
    label,
    labelPlural: normalizeString(row.labelPlural) || label,
    iconKey: toLowerSnakeCase(normalizeString(row.iconKey)),
    filters,
    initialFields: field && value ? { [field]: value } : {},
    defaultSurface: normalizeAppearAsDefaultSurface(row.defaultSurface),
    showInMenu: row.showInMenu !== false,
    showInAddMenu: row.showInAddMenu !== false,
  };
}

/**
 * @param {object|string|null|undefined} typeDefOrRows
 * @returns {object[]}
 */
function collectAppearAsRows(typeDefOrRows, {
  requireLabel = true,
  requireFilterValues = requireLabel,
} = {}) {
  const raw = Array.isArray(typeDefOrRows)
    ? typeDefOrRows
    : (Array.isArray(typeDefOrRows?.appearAs) ? typeDefOrRows.appearAs : []);

  const seen = new Set();
  const rows = [];
  raw.forEach((entry) => {
    const row = normalizeAppearAsRow(entry, { requireLabel, requireFilterValues });
    if (!row || seen.has(row.key)) return;
    seen.add(row.key);
    rows.push(row);
  });
  return rows;
}

/**
 * @param {object|string|null|undefined} typeDefOrRows
 * @returns {object[]}
 */
export function getTypeAppearAsRows(typeDefOrRows) {
  return collectAppearAsRows(typeDefOrRows, { requireLabel: true });
}

/**
 * Built-in Appear-as rows when a type has none stored yet.
 *
 * @param {object|string|null|undefined} typeDefOrKey
 * @returns {object[]}
 */
export function getDefaultAppearAsRows(typeDefOrKey) {
  const typeKey = String(
    (typeof typeDefOrKey === 'string' ? typeDefOrKey : typeDefOrKey?.key) || '',
  ).trim().toUpperCase();
  if (typeKey === 'PROJECT') {
    return getTypeAppearAsRows(buildProjectAppearAsRows());
  }
  return [];
}

/**
 * Stored appearAs when present; otherwise type-specific defaults.
 *
 * @param {object|null|undefined} typeDef
 * @returns {object[]}
 */
export function resolveTypeAppearAsRows(typeDef) {
  if (Array.isArray(typeDef?.appearAs)) {
    return getTypeAppearAsRows(typeDef);
  }
  return getDefaultAppearAsRows(typeDef);
}

/**
 * Keeps in-progress editor rows that do not have a label yet.
 *
 * @param {object|Array|null|undefined} typeDefOrRows
 * @returns {object[]}
 */
export function getTypeAppearAsDraftRows(typeDefOrRows) {
  return collectAppearAsRows(typeDefOrRows, {
    requireLabel: false,
    requireFilterValues: false,
  });
}

/**
 * @param {object|null|undefined} typeDef
 * @param {string} appearAsKey
 * @returns {object|null}
 */
export function resolveTypeAppearAs(typeDef, appearAsKey) {
  const key = normalizeAppearAsKey(appearAsKey);
  if (!key) return null;
  return resolveTypeAppearAsRows(typeDef).find((row) => row.key === key) || null;
}

/**
 * @param {object|null|undefined} typeDef
 * @returns {object[]}
 */
export function getMenuAppearAsRows(typeDef) {
  return resolveTypeAppearAsRows(typeDef).filter((row) => row.showInMenu);
}

/**
 * @param {object|null|undefined} typeDef
 * @returns {object[]}
 */
export function getAddMenuAppearAsRows(typeDef) {
  return resolveTypeAppearAsRows(typeDef).filter((row) => row.showInAddMenu);
}

/**
 * @param {object|null|undefined} typeDef
 * @returns {boolean}
 */
export function typeHasMenuAppearAs(typeDef) {
  return getMenuAppearAsRows(typeDef).length > 0;
}

/**
 * Types that should be considered for + Add expansion (own row and/or appearances).
 *
 * @param {object|null|undefined} typeDef
 * @returns {boolean}
 */
export function typeHasAddMenuPresence(typeDef) {
  return typeShowsInAddMenu(typeDef) || getAddMenuAppearAsRows(typeDef).length > 0;
}

/**
 * Expands type defs into + Add rows. Appearances come first, then the type row
 * when Show in Add menu is on.
 *
 * @param {object[]} typeDefs
 * @returns {object[]}
 */
export function expandAddMenuRows(typeDefs = []) {
  const rows = [];
  (Array.isArray(typeDefs) ? typeDefs : []).forEach((typeDef) => {
    const typeKey = normalizeString(typeDef?.key);
    if (!typeKey) return;

    getAddMenuAppearAsRows(typeDef).forEach((appear) => {
      rows.push({
        key: typeKey,
        menuKey: `${typeKey}::${appear.key}`,
        label: appear.label,
        iconKey: appear.iconKey || typeDef.iconKey || 'deployed_code',
        appearAsKey: appear.key,
        initialFields: appear.initialFields,
        typeDef,
      });
    });

    if (typeShowsInAddMenu(typeDef)) {
      rows.push({
        key: typeKey,
        menuKey: typeKey,
        label: normalizeString(typeDef.label) || typeKey,
        iconKey: typeDef.iconKey || 'deployed_code',
        appearAsKey: '',
        initialFields: null,
        typeDef,
      });
    }
  });
  return rows;
}

/**
 * @param {object|null|undefined} row
 * @returns {string}
 */
export function getAppearAsFilterField(row) {
  return normalizeString(row?.filters?.[0]?.field)
    || normalizeString(Object.keys(row?.initialFields || {})[0]);
}

/**
 * @param {object|null|undefined} row
 * @returns {string[]}
 */
export function getAppearAsFilterValues(row) {
  const value = getAppearAsValue(row);
  return value ? [value] : [];
}

/**
 * @param {object|null|undefined} row
 * @returns {string}
 */
export function getAppearAsValue(row) {
  const field = getAppearAsFilterField(row);
  if (field) {
    const stamped = row?.initialFields?.[field];
    if (Array.isArray(stamped)) return normalizeString(stamped[0]);
    const fromInitial = normalizeString(stamped);
    if (fromInitial) return fromInitial;
  }
  return normalizeStringList(row?.filters?.[0]?.value)[0] || '';
}

/**
 * @param {object|null|undefined} row
 * @returns {string}
 */
export function getAppearAsDefaultValue(row) {
  return getAppearAsValue(row);
}

/**
 * @param {object} row
 * @param {object} patch
 * @returns {object}
 */
export function applyAppearAsFilterDraft(row, {
  field = '',
  value = '',
  values = [],
  defaultValue = '',
} = {}) {
  const nextField = normalizeString(field);
  const nextValue = normalizeString(value || defaultValue || values[0]);
  const filters = nextField
    ? [{
      field: nextField,
      operator: 'is',
      value: nextValue,
      unit: null,
      type: 'enum',
      joinToNext: 'and',
    }]
    : [];
  const initialFields = nextField && nextValue
    ? { [nextField]: nextValue }
    : {};

  return normalizeAppearAsRow({
    ...row,
    filters,
    initialFields,
  }, { requireLabel: false, requireFilterValues: false }) || {
    ...row,
    filters,
    initialFields,
  };
}

/**
 * Enum / pipeline fields that can drive an Appear-as filter.
 *
 * @param {object[]} schemaFields
 * @returns {object[]}
 */
export function getAppearAsFilterFields(schemaFields = []) {
  return (Array.isArray(schemaFields) ? schemaFields : [])
    .filter((field) => {
      const name = normalizeString(field?.name || field?.key);
      return name && isEnumCompatibleFieldType(field);
    })
    .map((field) => ({
      name: normalizeString(field.name || field.key),
      label: normalizeString(field.label) || normalizeString(field.name || field.key),
      options: getSelectableEnumOptions(field.options).map((option) => ({
        ...option,
        label: getOptionLabel(option) || option.value,
      })),
    }));
}

/**
 * Allocates a unique appearAs key from a label.
 *
 * @param {string} label
 * @param {string[]} existingKeys
 * @returns {string}
 */
export function allocateAppearAsKey(label, existingKeys = []) {
  const used = new Set(
    (Array.isArray(existingKeys) ? existingKeys : []).map((key) => normalizeAppearAsKey(key)),
  );
  const base = normalizeAppearAsKey(label) || 'appearance';
  if (!used.has(base)) return base;
  let index = 2;
  while (used.has(`${base}_${index}`)) {
    index += 1;
  }
  return `${base}_${index}`;
}

/**
 * @returns {object}
 */
export function createBlankAppearAsRow(existingKeys = []) {
  return {
    key: allocateAppearAsKey('appearance', existingKeys),
    label: '',
    labelPlural: '',
    iconKey: '',
    filters: [],
    initialFields: {},
    defaultSurface: COLLECTION_SURFACE_RECORDS,
    showInMenu: true,
    showInAddMenu: true,
  };
}
